import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, type OnInit, Output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import type { AppState } from '@app/store';
import { getLanguages, getSubjects } from '@app/store/reducers/content.reducer';
import { AlertModule } from '@core/components/alert/alert.module';
import { logger } from '@core/helpers/logger';
import { PipesModule } from '@core/pipes/pipes.module';
import { AccountService, BillingV2Service, ContextService, FilesService, UserServicesService } from '@core/services';
import { HelpersBffService } from '@core/services/bff/helpers.service';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { IconComponent } from '@shared/icon/icon.component';
import { RatingComponent } from '@shared/rating/rating.component';
import {
  EUserServiceType,
  ImageSizeEnum,
  ModerationStatusEnum,
  User,
  UserService,
  UserServiceStateEnum,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiButtonModule, OnUiCardMenuModule, OnUiCover, OnUiCoverModule } from 'onclass-ui';
import { combineLatest, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { i18nService as TranslateService } from '../../../../i18n/translate.service';

const daysOfWeek = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'];

interface IStatusOption {
  value: UserServiceStateEnum;
  label: string;
}

@Component({
  selector: 'app-library-service-mini',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    RouterModule,

    OnUiCoverModule,
    OnUiButtonModule,
    OnUiCardMenuModule,
    PipesModule,
    AlertModule,
    RatingComponent,
    IconComponent,
  ],
  templateUrl: './library-service-mini.component.html',
  styleUrls: ['./library-service-mini.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LibraryServiceMiniComponent extends ChangableComponent implements OnInit {
  deviceService = inject(DeviceDetectorService);
  private contextService = inject(ContextService);
  private translate = inject(TranslateService);
  private userServicesService = inject(UserServicesService);
  private billing2Service = inject(BillingV2Service);
  private store = inject<Store<AppState>>(Store);
  private helpers = inject(HelpersBffService);

  @Input()
  public set service(service: UserService) {
    this._service = new UserService(service);
    this.prepareCard();
  }
  public get service(): UserService {
    return this._service;
  }
  @Input() isSchoolClasses: boolean;
  @Output() clicked = new EventEmitter<{ type: string; service: UserService }>();
  @Output() changed = new EventEmitter<void>();
  @Output() selected = new EventEmitter<boolean>();
  @Output() openUserServiceInModal = new EventEmitter<UserService>();

  public eUserServiceType = EUserServiceType;
  public userServiceRoute: string;
  public alertOpened: boolean;
  public cover: OnUiCover;
  public isOpenStats: boolean;
  public debug$ = this.contextService.debug$;
  private _service: UserService;

  public moderationStatuses: IStatusOption[] = [
    {
      value: UserServiceStateEnum.on_moderation,
      label: 'library.user-service-card.status.on-moderation',
    },
    {
      value: UserServiceStateEnum.archived,
      label: 'library.user-service-card.status.archived',
    },
    {
      value: UserServiceStateEnum.declined,
      label: 'library.user-service-card.status.declined',
    },
  ];

  ngOnInit(): void {
    combineLatest([this.store.select(getSubjects), this.store.select(getLanguages)])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([subjects, languages]) => {
          this.service.subject = subjects.find((el) => el.id == this.service.subject_id);
          this.service.language = languages.find((el) => el.id == this.service.language_id);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public typeLabel(type: EUserServiceType): string {
    switch (type) {
      case EUserServiceType.single:
        return 'library.user-service-card.type.single';
      case EUserServiceType.regular:
        return 'library.user-service-card.type.regular';
      case EUserServiceType.mini:
        return 'library.user-service-card.type.mini';
      case EUserServiceType.course:
        return 'library.user-service-card.type.course';
      default:
        return '';
    }
  }

  public labelPeriod(): string {
    switch (this._service.type) {
      case EUserServiceType.single:
        return 'library.user-service-card.label-period.single';
      case EUserServiceType.regular:
        return 'library.user-service-card.label-period.regular';
      case EUserServiceType.mini:
        return 'library.user-service-card.label-period.mini';
      case EUserServiceType.course:
        return 'library.user-service-card.label-period.course';
    }
  }

  public participants(): string {
    switch (this.service?.participants_limit) {
      case 1:
        return 'library.user-service-card.one-on-one';
      default:
        return 'library.user-service-card.group';
    }
  }

  public getFormattedAmount(): Observable<string> {
    return this.billing2Service.formatAmount$(this.service?.price_tier);
  }

  public weekdaysToString(weekdays: number[]): string {
    return weekdays?.map((_d) => this.translate.directInstant('days-of-week.' + daysOfWeek[_d])).join(', ');
  }

  public get showReasonMessage(): boolean {
    return this._service.moderation_message && this.moderationStatus.value === UserServiceStateEnum.declined;
  }

  public get moderationStatus(): IStatusOption {
    if (this._service.is?.declined) {
      return this.moderationStatuses.find((el) => el.value === UserServiceStateEnum.declined);
    } else if (this._service.is?.archived) {
      return this.moderationStatuses.find((el) => el.value === UserServiceStateEnum.archived);
    } else if (this._service.is?.on_moderation) {
      return this.moderationStatuses.find((el) => el.value === UserServiceStateEnum.on_moderation);
    } else {
      return { value: UserServiceStateEnum.on_moderation, label: '' };
    }
  }

  public signUpStatus() {
    if (this._service.is?.approved) {
      if (this._service.can.signup) {
        return {
          value: 'available',
          label: 'library.user-service-card.signup-status.available',
        };
      } else {
        if (
          (this.service.type === EUserServiceType.single || this.service.type === EUserServiceType.regular) &&
          this.service.is.individual
        ) {
          return {
            value: 'schedule',
            label: 'onboarding.checklist.schedule',
            link: '/schedule/working-hours',
          };
        } else {
          return {
            value: 'schedule',
            label: 'library.user-service-card.signup-status.schedule',
            link: '/schedule/calendar',
          };
        }
      }
    }
    return { value: null, label: '' };
  }

  public onOpenCardInModal() {
    this.openUserServiceInModal.emit(this.service);
  }

  public formatAge(age: number) {
    return age > 65 ? '65+' : age;
  }

  public onOpenAlert() {
    this.alertOpened = true;
  }

  public onRemove() {
    this.userServicesService
      .removeUserService({ id: this.service.id })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.changed.emit();
          this.alertOpened = false;
        },
        error: (error) => {
          this.changed.emit();
          this.alertOpened = false;
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  public onRestore() {
    this.userServicesService
      .restoreUserService(this.service.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.changed.emit();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onEdit() {
    this.selected.emit(true);
  }

  public get isRegularClass(): boolean {
    return this.service?.type === EUserServiceType.regular;
  }

  public printDebugInfo() {
    logger.log({
      service: this.service,
    });
  }

  public toggleSchoolStats() {
    this.isOpenStats = !this.isOpenStats;
  }

  public get teachers(): User[] {
    return this.service?.teachers;
  }

  public trackByFn(index: number, user: User): string {
    return user.id;
  }

  public teacherAvatar(teacher: User): OnUiCover {
    return {
      type: 'cover',
      img: FilesService.getFileUrlBySize(teacher?.userpic_id, ImageSizeEnum.sm),
      title: AccountService.getUserFullName(teacher),
      form: 'circle',
    };
  }

  private prepareCard() {
    this.userServiceRoute = this.helpers.userServiceRouteUniversal(this.service);
    if (this.service.moderation_status !== ModerationStatusEnum.approved) {
      this.service.can.edit = false;
      this.service.can.archive = false;
    }
    this.cover = {
      type: 'cover',
      img: this.service?.cover
        ? this.prepareStorageUrl(this.service?.cover.wide?.url || this.service?.cover.md?.url)
        : FilesService.getFileUrlBySize(this.service?.cover_id, ImageSizeEnum.wide),
      title: this.service.title,
      link: this.userServiceRoute,
      form: 'base',
    };
  }
}
